import NextLink from "next/link";
import { Box, Link, IconFa } from "@cruk/cruk-react-components";
import { faPeopleGroup } from "@fortawesome/free-solid-svg-icons";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { fwsUrlPageParentMemberships } from "@fwa/src/services/fundraisingPageService";
import { useOptimizelyContext } from "@fwa/src/contexts/OptimizelyContext";
import { useTracking } from "@fwa/src/hooks/useTracking";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/PageMemberOf/styles.module.css";

import type {
  PageEntityType,
  FwsResponseData,
  PageType,
  TeamMemberType,
} from "@fwa/src/types";

export const PageMemberOf = ({
  page,
  fallbackData,
  canEdit = false,
}: {
  page: PageType;
  fallbackData?: FwsResponseData<TeamMemberType>;
  canEdit?: boolean;
}) => {
  const { trackEventGtm } = useTracking();
  const { trackEventOptimizely } = useOptimizelyContext();
  const url = page
    ? `${fwsUrlPageParentMemberships({
        pageId: page.uniqueId,
        currentPage: 1,
        itemsPerPage: 3,
      })}`
    : null;
  const fallbackResponse = fallbackData;
  const { data } = useSwrGet<FwsResponseData<TeamMemberType>>(url, {
    fallbackData: fallbackResponse,
    revalidateOnMount: true,
  });
  const memberships = data?.results || [];
  const firstSuperteamMembership = memberships.find(
    (membership) =>
      membership?.parentPage?.entityType === ("SuperTeam" as PageEntityType),
  );
  const firstTeamMembership = memberships.find(
    (membership) =>
      membership?.parentPage?.entityType === ("Team" as PageEntityType),
  );
  const hasMemberships = firstSuperteamMembership || firstTeamMembership;
  const hasTeamAndSuperteamMemberships =
    firstSuperteamMembership && firstTeamMembership;
  const isMyFundraisingPage = page?.entityType === "FundraisingPage" && canEdit;

  return (
    <>
      {hasMemberships && (
        <div className={sharedStyles["activity-item-wrapper"]}>
          <div className={sharedStyles["activity-item"]}>
            <div
              className={sharedStyles["activity-icon-wrapper"]}
              data-is-empty={!hasMemberships}
            >
              <IconFa faIcon={faPeopleGroup} size="100%" />
            </div>
            <Box marginRight="s">
              <p className={styles["text-responsive"]}>
                {` A member of `}
                {firstSuperteamMembership?.parentPage.title && (
                  <NextLink
                    href={firstSuperteamMembership.parentPage.url.replace(
                      /https?:\/\/[^/]*/,
                      "",
                    )}
                    data-cta-type="link-member-superteam"
                  >
                    <Link as="span">
                      {firstSuperteamMembership.parentPage.title}
                    </Link>
                  </NextLink>
                )}
              </p>

              {firstTeamMembership?.parentPage.title ? (
                <>
                  <p className={styles["text-responsive"]}>
                    {hasTeamAndSuperteamMemberships && " and "}
                    <NextLink
                      href={firstTeamMembership.parentPage.url.replace(
                        /https?:\/\/[^/]*/,
                        "",
                      )}
                      data-cta-type="link-member-team"
                    >
                      <Link as="span">
                        {firstTeamMembership.parentPage.title}
                      </Link>
                    </NextLink>
                  </p>
                </>
              ) : isMyFundraisingPage ? (
                <>
                  <p className={styles["text-responsive"]}>
                    <NextLink
                      href={`/team-search?fp=${page.uniqueId}`}
                      onClick={() => {
                        trackEventOptimizely({
                          eventKey: "become_part_of_a_team_click",
                        });
                        trackEventGtm({
                          event: "join_team_cta",
                          cta: "fundraising_page",
                        });
                      }}
                      data-cta-type="join-team"
                    >
                      <Link as="span">Create or join a team</Link>
                    </NextLink>
                  </p>
                </>
              ) : null}
            </Box>
          </div>
        </div>
      )}

      {!hasMemberships && isMyFundraisingPage ? (
        <div
          className={sharedStyles["activity-item-wrapper"]}
          data-component="member-of-team-search-cta"
        >
          <NextLink
            href={`/team-search?fp=${page.uniqueId}`}
            onClick={() => {
              trackEventOptimizely({ eventKey: "become_part_of_a_team_click" });
              trackEventGtm({
                event: "join_team_cta",
                cta: "fundraising_page",
              });
            }}
            data-cta-type="join-team"
          >
            <span className={styles["activity-link-span"]}>
              <div
                className={sharedStyles["activity-item"]}
                data-can-edit={isMyFundraisingPage}
              >
                <div
                  className={sharedStyles["activity-icon-wrapper"]}
                  data-is-empty={!hasMemberships}
                >
                  <IconFa faIcon={faPeopleGroup} size="100%" />
                </div>

                <Box>
                  <Link as="span">Create or join a team</Link>
                </Box>
              </div>
            </span>
          </NextLink>
        </div>
      ) : null}
    </>
  );
};

export default PageMemberOf;
